/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */

'use client';

import { useCallback, useState } from 'react';
import type { Filter } from '@cvent/venue-search-shared/types';
import { useApplyFilter } from '@/hooks/useApplyFilter';
import { CSN_SEARCH_BANNER_COOKIE_NAME } from '@/constants/cookieConstants';
import { useRouter } from '@/lib/router-events/patch-router/router';
import Icon from '@/components/shared/Icons';
import { FILTERS_TO_RESET } from '@/constants/filterConstants';

export type RfpBannerProps = {
  rfpBannerTitle: string;
  rfpBannerMessageWithMeetings: string;
  rfpBannerMessageWithGuestRooms: string;
  rfpBaannerMessageWithMeetingsAndGuestRooms: string;
  rfpBannerResetFilters: string;
  rfpBannerCloseAriaLabel: string;
};

type Props = {
  searchTerm: string;
  appliedFilters: Filter;
  rfpBannerContent: RfpBannerProps;
  rfpId: string | undefined;
  meetingRooms: boolean;
  guestRooms: boolean;
};

// RFP Banner is visible and filters are applied when user comes from rfp context
export default function RfpBanner({
  searchTerm,
  appliedFilters,
  rfpBannerContent,
  rfpId,
  meetingRooms,
  guestRooms
}: Props): JSX.Element {
  const router = useRouter();
  const {
    rfpBannerTitle,
    rfpBannerMessageWithMeetings,
    rfpBannerMessageWithGuestRooms,
    rfpBaannerMessageWithMeetingsAndGuestRooms,
    rfpBannerResetFilters,
    rfpBannerCloseAriaLabel
  } = rfpBannerContent;

  const { applyFilters } = useApplyFilter();
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const handleResetFilterClick = useCallback(async () => {
    const updatedFilters = { ...appliedFilters };
    FILTERS_TO_RESET.forEach(filter => {
      delete updatedFilters[filter];
    });
    if (document) {
      document.cookie = `${CSN_SEARCH_BANNER_COOKIE_NAME}=${JSON.stringify({
        isLoginBannerDismissed: false,
        dismissedRfpBannerId: rfpId
      })}`;
    }
    setIsBannerVisible(false);
    await applyFilters(searchTerm, updatedFilters, true, 'CVENT_PICKS', undefined, undefined, true, true);
  }, [appliedFilters, applyFilters, rfpId, searchTerm]);

  const onRfpBannerClose = useCallback(
    e => {
      e.preventDefault();
      if (document) {
        document.cookie = `${CSN_SEARCH_BANNER_COOKIE_NAME}=${JSON.stringify({
          isLoginBannerDismissed: false,
          dismissedRfpBannerId: rfpId
        })}`;
      }
      setIsBannerVisible(false);
      router.refresh();
    },
    [router]
  );

  return isBannerVisible ? (
    <div className="block w-full justify-between rounded-xl bg-brand-20 shadow-2">
      <div className="flex flex-row items-center" data-cvent-id="rfp-banner">
        <div className="-mt-6 ml-3 mr-2">
          <Icon id="info" className="h-6 w-6 fill-interactive-70" testID="RFP-banner-information-icon" />
        </div>
        <div className="block w-full flex-row justify-between md:flex">
          <div className="">
            <div className="m-4 text-b-md font-medium">{rfpBannerTitle}</div>
            {meetingRooms && !guestRooms && (
              <div className="m-4 -mt-2 text-b-md font-normal">{rfpBannerMessageWithMeetings}</div>
            )}
            {!meetingRooms && guestRooms && (
              <div className="m-4 -mt-2 text-b-md font-normal">{rfpBannerMessageWithGuestRooms}</div>
            )}
            {meetingRooms && guestRooms && (
              <div className="m-4 -mt-2 text-b-md font-normal">{rfpBaannerMessageWithMeetingsAndGuestRooms}</div>
            )}
          </div>
          <div
            className="medium mb-3 ml-4 mt-1 flex text-b-md text-interactive-70 md:pt-4"
            role="button"
            tabIndex={0}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                handleResetFilterClick();
              }
            }}
            aria-label={rfpBannerResetFilters}
            onClick={handleResetFilterClick}
            data-cvent-id={rfpBannerResetFilters}
          >
            {rfpBannerResetFilters}
          </div>
        </div>
        <div className="-mt-5 ml-3.5 mr-5 flex h-fit w-fit justify-end">
          <button type="button" aria-label={rfpBannerCloseAriaLabel} onClick={onRfpBannerClose}>
            <span>
              <Icon id="x" data-cvent-id="close-rfp-banner" className="h-6 w-6" />
            </span>
          </button>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
}
